import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Expand } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/establishments",
)({ component: RouteComponent });

function RouteComponent() {
	const { t, i18n } = useTranslation();
	const permissionsTitleId = useId();
	const locale = i18n.language === "en_US" ? "en-US" : "pt-BR";
	const sections = [
		{
			id: "selector",
			title: t("help.farmGuide.selectorTitle"),
			intro: t("help.farmGuide.selectorIntro"),
			steps: [
				t("help.farmGuide.selectorStep1"),
				t("help.farmGuide.selectorStep2"),
				t("help.farmGuide.selectorStep3"),
			],
			note: t("help.farmGuide.selectorNote"),
			caption: t("help.farmGuide.selectorCaption"),
			width: 2400,
			height: locale === "en-US" ? 842 : 840,
		},
		{
			id: "management",
			title: t("help.farmGuide.managementTitle"),
			intro: t("help.farmGuide.managementIntro"),
			steps: [
				t("help.farmGuide.managementStep1"),
				t("help.farmGuide.managementStep2"),
				t("help.farmGuide.managementStep3"),
			],
			caption: t("help.farmGuide.managementCaption"),
			width: 2400,
			height: 1304,
		},
		{
			id: "create",
			title: t("help.farmGuide.createTitle"),
			intro: t("help.farmGuide.createIntro"),
			steps: [
				t("help.farmGuide.createStep1"),
				t("help.farmGuide.createStep2"),
				t("help.farmGuide.createStep3"),
				t("help.farmGuide.createStep4"),
			],
			note: t("help.farmGuide.createNote"),
			caption: t("help.farmGuide.createCaption"),
			width: 1200,
			height: 1080,
		},
		{
			id: "edit",
			title: t("help.farmGuide.editTitle"),
			intro: t("help.farmGuide.editIntro"),
			steps: [
				t("help.farmGuide.editStep1"),
				t("help.farmGuide.editStep2"),
				t("help.farmGuide.editStep3"),
			],
			note: t("help.farmGuide.editNote"),
			caption: t("help.farmGuide.editCaption"),
			width: 1200,
			height: 1080,
		},
	];

	return (
		<article className="mx-auto max-w-4xl space-y-8 p-4 text-base-content sm:p-8">
			<Link
				to="/app/help/user-guide"
				className="inline-flex items-center gap-2 text-sm text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
			>
				<ArrowLeft className="size-4" aria-hidden="true" />
				{t("help.userGuide")}
			</Link>
			<header className="max-w-prose space-y-4">
				<h1 className="text-3xl font-bold sm:text-4xl">
					{t("help.guideEstablishmentsTitle")}
				</h1>
				<p className="text-base leading-relaxed">{t("help.farmGuide.intro")}</p>
			</header>
			<nav
				aria-label={t("help.farmGuide.contents")}
				className="border-y border-base-300 py-5"
			>
				<h2 className="mb-3 font-semibold">{t("help.farmGuide.contents")}</h2>
				<ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
					{sections.map((section) => (
						<li key={section.id}>
							<a
								href={`#${section.id}`}
								className="text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								{section.title}
							</a>
						</li>
					))}
				</ul>
			</nav>
			<p className="text-sm text-base-content/70">
				{t("help.farmGuide.screenshotsNote")}
			</p>
			{sections.map((section) => {
				const src = `/help/farms/${section.id}-${locale}.webp`;
				return (
					<section
						key={section.id}
						id={section.id}
						aria-labelledby={`${section.id}-title`}
						className="scroll-mt-6 space-y-5 border-b border-base-300 pb-8"
					>
						<div className="max-w-prose space-y-3">
							<h2
								id={`${section.id}-title`}
								className="text-xl font-semibold sm:text-2xl"
							>
								{section.title}
							</h2>
							<p className="leading-relaxed">{section.intro}</p>
							<ol className="list-decimal space-y-3 pl-5 leading-relaxed marker:font-semibold marker:text-primary">
								{section.steps.map((step) => (
									<li key={step} className="pl-1">
										{step}
									</li>
								))}
							</ol>
						</div>
						<figure className="space-y-3">
							<a
								href={src}
								target="_blank"
								rel="noreferrer"
								aria-label={`${t("help.farmGuide.enlargeImage")}: ${section.title}`}
								className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								<img
									src={src}
									alt={section.caption}
									width={section.width}
									height={section.height}
									loading="lazy"
									decoding="async"
									className="h-auto w-full rounded-lg border border-base-300"
								/>
							</a>
							<figcaption className="space-y-2 text-sm leading-relaxed text-base-content/70">
								<p>{section.caption}</p>
								<a
									href={src}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
								>
									<Expand className="size-4" aria-hidden="true" />
									{t("help.farmGuide.enlargeImage")}
								</a>
							</figcaption>
						</figure>
						{section.note && (
							<p className="max-w-prose rounded-lg bg-base-100 p-4 text-sm leading-relaxed">
								{section.note}
							</p>
						)}
					</section>
				);
			})}
			<section
				aria-labelledby={permissionsTitleId}
				className="max-w-prose space-y-3"
			>
				<h2 id={permissionsTitleId} className="text-xl font-semibold">
					{t("help.farmGuide.permissionsTitle")}
				</h2>
				<p className="leading-relaxed">{t("help.farmGuide.permissionsBody")}</p>
			</section>
		</article>
	);
}
